import uuid from 'uuid-random';
import { pathOr } from 'ramda';
import { toDate, parseISO } from 'date-fns';

export function parseLines(data) {
  return data.map(row => ({
    description: row.description,
    id: row.id,
    identifier: uuid(),
    position: row.position,
    price: parseFloat(row.price),
    quantity: parseFloat(row.quantity),
    unit_type: row.unit_type,
    vat_rate: parseInt(row.vat_rate, 10),
    discount_amount: parseFloat(row.discount_amount),
    discount_kind: row.discount_kind,
    is_discount_line: row.is_discount_line,
    _destroy: false,
  }));
}

export const replaceEmptyDataWithDefault = (state, fetched) => {
  const filtered = Object.keys(fetched).reduce((acc, val) => {
    if (fetched[val] !== null) {
      return {
        ...acc,
        [val]: fetched[val],
      };
    }
    return acc;
  }, {});

  return {
    ...state,
    ...filtered,
  };
};

export default function mapInvoiceDataToForm(data) {
  const result = {
    buyer_id: pathOr(null, ['buyer', 'id'], data),
    buyer_address_city: pathOr('', ['buyer', 'city'], data),
    buyer_address_country_code: pathOr(null, ['buyer', 'country_code'], data),
    buyer_address_subdivision: pathOr(null, ['buyer', 'subdivision'], data),
    buyer_address_postcode: pathOr('', ['buyer', 'postcode'], data),
    buyer_address_street: pathOr('', ['buyer', 'street'], data),
    buyer_company_number: pathOr('', ['buyer', 'company_number'], data),
    buyer_company_name: pathOr(null, ['contact_id'], data),
    buyer_name: pathOr('', ['buyer', 'name'], data),
    buyer_tax_number: pathOr('', ['buyer', 'tax_number'], data),
    buyer_vat_number: pathOr('', ['buyer', 'vat_number'], data),
    buyer_email: pathOr('', ['buyer', 'email'], data),
    buyer_phone: pathOr('', ['buyer', 'phone'], data),
    buyer_web: pathOr('', ['buyer', 'web'], data),
    constant_symbol: data.constant_symbol,
    color: data.color,
    currency: data.currency,
    currency_vat_conversion: null,
    design: parseInt(data.design, 10),
    due_in: pathOr('14', ['due_in_api'], data),
    delivery_on: data?.delivery_on ? toDate(parseISO(data.delivery_on)) : null,
    due_on_custom: toDate(parseISO(data.due_on)),
    foot_note: data.foot_note,
    issued_by: data.issued_by,
    issued_on: toDate(parseISO(data.issued_on)),
    kind: data.kind,
    language: data.language,
    lines: parseLines(data.lines),
    logo: pathOr('', ['logo', 'url'], data),
    logo_id: pathOr('', ['logo_id'], data),
    means_of_payment: data.means_of_payment,
    note: data.note,
    payment_symbol: data.payment_symbol,
    specific_symbol: data.specific_symbol,
    rounding_type: data.rounding_type,
    reference: data.reference,
    seller_id: pathOr(null, ['seller', 'id'], data),
    seller_address_city: pathOr('', ['seller', 'city'], data),
    seller_address_country_code: pathOr(null, ['seller', 'country_code'], data),
    seller_address_subdivision: pathOr(null, ['seller', 'subdivision'], data),
    seller_address_postcode: pathOr('', ['seller', 'postcode'], data),
    seller_address_street: pathOr('', ['seller', 'street'], data),
    seller_bank_account_iban: pathOr('', ['seller', 'iban'], data),
    seller_bank_account_number: pathOr('', ['seller', 'bank_account_number'], data),
    seller_bank_account_show_iban: pathOr(null, ['seller', 'show_iban'], data),
    seller_bank_account_swift: pathOr('', ['seller', 'swift'], data),
    seller_bank_account_routing_number: pathOr('', ['seller', 'routing_number'], data),
    seller_bank_account_sort_code: pathOr('', ['seller', 'sort_code'], data),
    seller_company_number: pathOr('', ['seller', 'company_number'], data),
    seller_name: pathOr('', ['seller', 'name'], data),
    seller_tax_number: pathOr('', ['seller', 'tax_number'], data),
    seller_vat_number: pathOr('', ['seller', 'vat_number'], data),
    seller_email: pathOr('', ['seller', 'email'], data),
    seller_phone: pathOr('', ['seller', 'phone'], data),
    seller_web: pathOr('', ['seller', 'web'], data),
    show_qr_code: data.show_qr_code,
    stamp: pathOr('', ['stamp', 'url'], data),
    state: data.state,
    stamp_id: pathOr('', ['stamp_id'], data),
    tax_point_on: toDate(parseISO(data.tax_point_on)),
    vat_calculation: data.vat_calculation,
    eet: data.eet,
    register_eet: data.register_eet,
    credit_noted_invoice_id: data.credit_noted_invoice_id,
    recurring_profile: data.recurring_profile,
    recurring_draft: data.recurring_draft,
    recurring_end_options: data.recurring_end_options,
    recurring_frequency: data.recurring_frequency,
    recurring_ends_on: data?.recurring_ends_on ? toDate(parseISO(data.recurring_ends_on)) : null,
    recurring_ends_after_count: data.recurring_ends_after_count,
    recurring_issue_last_day: data.recurring_issue_last_day,
    recurring_auto_submit: data.recurring_auto_submit,
    recurring_submit_copy: data.recurring_submit_copy,
    vat_totals_currency_conversion: data.vat_totals_currency_conversion,
  };

  if (pathOr(false, ['number'], data)) {
    result.number = data.number;
  }

  if (pathOr(false, ['number'], data)) {
    result.registration_number = data.registration_number;
  }

  if (data.id) {
    result.id = data.id;
  }

  if (pathOr(false, ['logo', 'thumb', 'url'], data)) {
    result.logo = [{
      url: data.logo.thumb.url,
      name: data.logo.filename,
    }];
  }

  if (pathOr(false, ['stamp', 'thumb', 'url'], data)) {
    result.stamp = [{
      url: data.stamp.thumb.url,
      name: data.stamp.filename,
    }];
  }

  return result;
}
